export interface DatasetDistribution {
  label: string;
  categories: readonly { name: string; count: number; description?: string }[];
  /** Shown below the chart (e.g. totals or non-mutually-exclusive categories). */
  note?: string;
  total?: number;
}

export interface DatasetSampleImage {
  class: 'A' | 'B' | 'C' | 'D';
  src: string;
  /** Short label shown in density guide cards. */
  title: string;
  description: string;
}

export interface DatasetPublicProfile {
  /** Total labelled samples (images or patient records). */
  totalSamples: number;
  participantCount?: number;
  imageCount?: number;
  /** Non-chart statistics such as mean ± SD age. */
  summaryStats?: readonly { label: string; value: string }[];
  distributions: readonly DatasetDistribution[];
  input: {
    dataType: string;
    size: string;
    format: string;
    maxFileSize?: string;
    organization?: string;
    preprocessing?: string;
  };
  output: {
    dataType: string;
    format: string;
    maxFileSize?: string;
    columns?: readonly string[];
    description?: string;
  };
  /** Optional de-identified example images, one per density class. */
  sampleImages?: readonly DatasetSampleImage[];
  acknowledgments?: string;
  version?: string;
}

export type ProblemStatementType =
  | 'binary_classification'
  | 'multiclass_classification'
  | 'segmentation'
  | 'bounding_box';

const PROBLEM_STATEMENT_LABELS: Record<ProblemStatementType, string> = {
  binary_classification: 'Binary classification',
  multiclass_classification: 'Multiclass classification',
  segmentation: 'Segmentation',
  bounding_box: 'Bounding box',
};

/** Human-readable label for a stored problem statement value; undefined when empty. */
export function formatProblemStatement(value: unknown): string | undefined {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return undefined;
  }
  return (
    PROBLEM_STATEMENT_LABELS[raw as ProblemStatementType] ??
    raw.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase())
  );
}

/** Icon/badge variant for a stored (or already-formatted) problem statement value. */
export type ProblemStatementKind = 'binary' | 'multiclass' | 'segmentation' | 'boundingBox' | 'other';

/** Classifies a problem statement value for badge/icon styling, independent of exact wording. */
export function getProblemStatementKind(value: unknown): ProblemStatementKind {
  const raw = String(value ?? '').toLowerCase();
  if (/segmentation/.test(raw)) {
    return 'segmentation';
  }
  if (/bounding.?box|object.?detection/.test(raw)) {
    return 'boundingBox';
  }
  if (/multiclass/.test(raw)) {
    return 'multiclass';
  }
  if (/binary/.test(raw)) {
    return 'binary';
  }
  return 'other';
}

export interface HackathonDataset {
  id: string;
  title: string;
  problemStatement: ProblemStatementType;
  /** Dataset id used by benchmark leaderboard API. */
  leaderboardDatasetId: string;
  modality: string;
  volume: string;
  output: string;
  /** First primary metric (backward compatible). */
  primaryMetric: string;
  /** Second primary evaluation metric shown on reference pages. */
  secondPrimaryMetric: string;
  secondaryMetric: string;
  /** Benchmark API key used for default leaderboard sort. */
  primaryMetricKey: string;
  /** PVC sent to challenge-evaluator-backend /leaderboards for Evaluate sandbox. */
  evaluatorSandboxPvc: string;
  description: string;
  /** Public-facing dataset card content (statistics, I/O, acknowledgments). */
  publicProfile?: DatasetPublicProfile;
}

/** Maps reference-page metric labels to benchmark leaderboard API keys. */
export const REFERENCE_METRIC_TO_LEADERBOARD_KEY: Record<string, string> = {
  'Sensitivity (Recall)': 'sensitivity',
  'Specificity (True Negative Rate)': 'specificity',
  Accuracy: 'accuracy',
  'PPV (Positive Predictive Value)': 'ppv',
  'NPV (Negative Predictive Value)': 'npv',
  'F2 Score': 'f2_score',
  'Macro F2': 'macro_f2',
  'Weighted F2': 'weighted_f2',
  'Macro F1': 'macro_f1',
  'QWK (Quadratic Weight Kappa)': 'qwk',
  'AUC (Area Under Curve)': 'auc',
  'FNR (False Negative Rate)': 'fnr',
  'FPR (False Positive Rate)': 'fpr',
};

/** Table 2: Reference datasets for TANUH hackathon / benchmark. */
export const HACKATHON_DATASETS: HackathonDataset[] = [
];

/** Evaluation metrics reported for each reference dataset (priority order from data cards). */
export const DATASET_REFERENCE_METRICS: Record<string, readonly string[]> = {};

/** Leaderboard column order derived from data-card priority (reference metric labels). */
export const DATASET_LEADERBOARD_METRIC_KEYS: Record<string, readonly string[]> = {};

export function getDatasetPublicProfile(
  datasetId: HackathonDataset['leaderboardDatasetId'] | undefined,
): DatasetPublicProfile | undefined {
  if (!datasetId) {
    return undefined;
  }
  return HACKATHON_DATASETS.find((ds) => ds.leaderboardDatasetId === datasetId)?.publicProfile;
}

export function getDatasetPrimaryMetrics(dataset: {
  primaryMetric?: string;
  secondPrimaryMetric?: string;
  leaderboardDatasetId?: HackathonDataset['leaderboardDatasetId'];
}): readonly string[] {
  if (dataset.secondPrimaryMetric?.trim()) {
    return [dataset.primaryMetric ?? '', dataset.secondPrimaryMetric].filter(Boolean);
  }

  const matched = dataset.leaderboardDatasetId
    ? HACKATHON_DATASETS.find((ds) => ds.leaderboardDatasetId === dataset.leaderboardDatasetId)
    : undefined;
  if (matched) {
    return [matched.primaryMetric, matched.secondPrimaryMetric];
  }

  return dataset.primaryMetric ? [dataset.primaryMetric] : [];
}

export function findHackathonDatasetBySlug(
  slug: string
): HackathonDataset | undefined {
  return HACKATHON_DATASETS.find((ds) => ds.leaderboardDatasetId === slug);
}

/** Match a catalogue API item to a TANUH reference dataset when possible. */
export function findHackathonDatasetForCatalogueItem(item: {
  id?: string;
  label?: string;
  name?: string;
  title?: string;
}): HackathonDataset | undefined {
  const id = (item.id ?? '').trim().toLowerCase();
  if (id) {
    const bySlug = findHackathonDatasetBySlug(id);
    if (bySlug) {
      return bySlug;
    }
  }

  const title = (item.label ?? item.name ?? item.title ?? '').trim().toLowerCase();
  if (!title) {
    return undefined;
  }

  return HACKATHON_DATASETS.find((ds) => {
    const dsTitle = ds.title.toLowerCase();
    const dsId = ds.id.toLowerCase();
    return title === dsTitle || title === dsId;
  });
}

export function getReferenceDatasetDetailLink(
  dataset: HackathonDataset,
  from: 'datasets' | 'reference' = 'reference'
): string {
  const base = `/reference/dataset/${dataset.leaderboardDatasetId}`;
  return from === 'datasets' ? `${base}?from=datasets` : base;
}

export function getCatalogueDatasetDetailLink(
  id: string,
  from: 'datasets' | 'reference' = 'datasets'
): string {
  return from === 'datasets' ? `/dataset/${id}?from=datasets` : `/dataset/${id}`;
}

/** View model for TANUH dataset detail (static reference or catalogue API). */
export interface TanuhDatasetDetailView {
  id: string;
  title: string;
  /** Formatted label, e.g. "Binary classification". */
  problemStatement?: string;
  modality: string;
  volume: string;
  output: string;
  primaryMetric: string;
  secondPrimaryMetric?: string;
  secondaryMetric: string;
  description: string;
  /** Hackathon dataset id for Submit Model onboard flow. */
  submitModelKey?: string;
  leaderboardDatasetId?: HackathonDataset['leaderboardDatasetId'];
}

export const DEFAULT_DATASET_EVALUATION_METRICS: readonly string[] = [
  'Accuracy',
  'Macro F2',
  'Weighted F2',
  'Macro F1',
  'Sensitivity (Recall)',
  'Specificity (True Negative Rate)',
  'NPV (Negative Predictive Value)',
  'PPV (Positive Predictive Value)',
];

export function hackathonDatasetToDetailView(ds: HackathonDataset): TanuhDatasetDetailView {
  return {
    id: ds.leaderboardDatasetId,
    title: ds.title,
    problemStatement: formatProblemStatement(ds.problemStatement),
    modality: ds.modality,
    volume: ds.volume,
    output: ds.output,
    primaryMetric: ds.primaryMetric,
    secondPrimaryMetric: ds.secondPrimaryMetric,
    secondaryMetric: ds.secondaryMetric,
    description: ds.description,
    submitModelKey: ds.id,
    leaderboardDatasetId: ds.leaderboardDatasetId,
  };
}

export function mapCatalogueItemToDatasetDetailView(
  item: Record<string, unknown>
): TanuhDatasetDetailView {
  const matched = findHackathonDatasetForCatalogueItem({
    id: item['id'] as string | undefined,
    label: item['label'] as string | undefined,
    name: item['name'] as string | undefined,
    title: item['title'] as string | undefined,
  });

  if (matched) {
    return hackathonDatasetToDetailView(matched);
  }

  const description =
    (item['shortDescription'] as string | undefined) ??
    (item['description'] as string | undefined) ??
    '';

  return {
    id: String(item['id'] ?? ''),
    title: String(item['label'] ?? item['name'] ?? item['id'] ?? 'Dataset'),
    description: typeof description === 'string' ? description : '',
    problemStatement: formatProblemStatement(item['problemStatement']),
    modality: String(item['modality'] ?? '—'),
    volume: item['volume'] != null && item['volume'] !== '' ? String(item['volume']) : '—',
    output: String(item['output'] ?? '—'),
    primaryMetric: String(item['primaryMetric'] ?? '—'),
    secondaryMetric: String(item['secondaryMetric'] ?? '—'),
  };
}
